import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref, createRef, Ref } from 'lit/directives/ref.js';

import suchwidgetAdressenStyles from '../scss/suchwidget-adressen.scss?inline';

const debounce = (func: Function, wait: number) => {
    let timeout: number | null = null;
    return function (...args: any[]) {
        const context = this;
        const later = () => {
            timeout = null;
            func.apply(context, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

const logoSVG = `${import.meta.env.VITE_SUCHWIDGET_BASE_PATH as string}static/img/familienratgeber_logo.svg`;

@customElement('familienratgeber-suchwidget-adressen')
export class SuchwidgetAdressen extends LitElement {
    static readonly shadowRootOptions = { ...LitElement.shadowRootOptions, mode: 'closed' as ShadowRootMode };
    static readonly styles = [
        unsafeCSS(suchwidgetAdressenStyles),
        css`
            .suchwidget-adressen .logo::before {
                background-image: url(${unsafeCSS(logoSVG)});
            }
        `,
    ];

    smallContainerSizeWidth = 340;
    containerRef: Ref<HTMLDivElement> = createRef();
    inputOrtRef: Ref<HTMLInputElement> = createRef();
    inputSchlagworteRef: Ref<HTMLInputElement> = createRef();

    @property()
    viewportClasses = {
        'suchwidget-adressen': true,
        'has-small-container-size': false,
    };

    @property()
    submitButtonClasses = {
        disabled: true,
    };

    @property()
    allowSubmit = false;

    inputChangedHandler() {
        const inputOrt = this.inputOrtRef.value;
        const inputSchlagworte = this.inputSchlagworteRef.value;

        if (inputOrt.value.trim() !== '' || inputSchlagworte.value.trim() !== '') {
            inputOrt.setCustomValidity('');
            this.allowSubmit = true;
            this.submitButtonClasses['disabled'] = false;
        } else {
            this.allowSubmit = false;
            this.submitButtonClasses['disabled'] = true;
        }
    }

    submitClickedHandler(e: Event) {
        if (!this.allowSubmit) {
            e.stopPropagation();
            e.preventDefault();

            const inputOrt = this.inputOrtRef.value;

            if (inputOrt.value.trim() === '') {
                inputOrt.setCustomValidity('Fülle dieses Feld aus.');
                inputOrt.reportValidity();
                inputOrt.focus();
            }
        }
    }

    getCurrentOrigin() {
        return document.location.origin;
    }

    debouncedResizeHandler = debounce(() => {
        if (this.containerRef.value!.offsetWidth < this.smallContainerSizeWidth) {
            this.viewportClasses['has-small-container-size'] = true;
        } else {
            this.viewportClasses['has-small-container-size'] = false;
        }
        this.requestUpdate();
    }, 50);

    firstUpdated() {
        if (this.containerRef.value!.offsetWidth < this.smallContainerSizeWidth) {
            this.viewportClasses['has-small-container-size'] = true;
            this.requestUpdate();
        }
        window.addEventListener('resize', this.debouncedResizeHandler);
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.debouncedResizeHandler);
        super.disconnectedCallback();
    }

    render() {
        return html`
            <div class="${classMap(this.viewportClasses)}" ${ref(this.containerRef)}>
                <a href="https://www.familienratgeber.de" target="_blank" rel="noopener">
                    <h3 class="logo">
                        <span class="sr-only">Familienratgeber</span>
                    </h3>
                </a>
                <form
                    action="${import.meta.env.VITE_SUCHWIDGET_BASE_PATH as string}beratung-und-hilfen/adressen-vor-ort/suchergebnis"
                    method="post"
                    accept-charset="utf-8"
                >
                    <h4>Adressen vor Ort</h4>
                    <p>
                        In der Adressdatenbank auf Familienratgeber.de finden Sie Anlauf- und Beratungsstellen für Menschen mit Behinderung und ihre
                        Angehörigen.
                    </p>

                    <input type="hidden" name="reset" />
                    <input type="hidden" name="searchPosition" value="widget" />
                    <input id="searchOrigin" type="hidden" name="searchOrigin" value="${this.getCurrentOrigin()}" />

                    <div class="input-container">
                        <input
                            type="text"
                            name="ort"
                            placeholder="Suche nach Postleitzahl / Ort"
                            autocomplete="off"
                            ${ref(this.inputOrtRef)}
                            @keyup="${this.inputChangedHandler}"
                        />
                    </div>
                    <div class="input-container">
                        <input
                            type="text"
                            name="schlagworte"
                            placeholder="Schlagwörter"
                            autocomplete="off"
                            ${ref(this.inputSchlagworteRef)}
                            @keyup="${this.inputChangedHandler}"
                        />
                    </div>
                    <button type="submit" class="button button--primary ${classMap(this.submitButtonClasses)}" @click="${this.submitClickedHandler}">
                        Suchen
                    </button>
                </form>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'familienratgeber-suchwidget-adressen': SuchwidgetAdressen;
    }
}
